import { useTranslation } from "react-i18next";
import { styled, InputAdornment, MenuItem, IconButton } from "@mui/material";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { MessagesFilterRequest } from "@/models/apiModels";
import BaseInput from "@/components/common/baseInput";
import BaseDateInput from "@/components/common/baseDateInput";
import ClearIcon from "@/assets/icons/clear-icon.svg";

import SearchIcon from "@mui/icons-material/Search";

interface MessagesFilterProps {
    searchClick: Function;
    filter: MessagesFilterRequest;
    setFilter: Function;
    total: number;
}

const SearchButton = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
            padding: 0px 20px;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
            padding: 0px 20px;
          }

          &.${buttonUnstyledClasses.root} {
            padding: 0px 20px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
            padding: 0px 20px;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
            padding: 0px 20px;
          }`
);

function MessagesFilter({ searchClick, filter, setFilter }: MessagesFilterProps) {
    const { t } = useTranslation();

    function handleSubmit(event: any) {
        event.preventDefault();
        searchClick();
    }

    const messageStatus = [
        {
            value: 1,
            label: t("message.ActiveStatus"),
        },
        {
            value: 2,
            label: t("message.InactiveStatus"),
        },
    ];

    function clearFilter() {
        setFilter(() => ({
            ...new MessagesFilterRequest(),
        }));
        searchClick();
    }

    return (
        <div id="accounting-filter-block" className="accounting-filter-block">
            <div className="accounting-filter">
                <form onSubmit={handleSubmit}>
                    <div className="font-style accounting-span">{t("accounting.FilterHeader")}</div>
                    <div className="accounting-filter-first-row">
                        <div className="accounting-filter-fields">
                            <div>
                                <BaseInput
                                    id="keyword-input"
                                    autoComplete="off"
                                    className="search-input"
                                    placeholder={`${t("accounting.FilterSearch")}`}
                                    type="search"
                                    size="small"
                                    value={filter.keywords}
                                    onChange={(event) =>
                                        setFilter(() => ({
                                            ...filter,
                                            keywords: event.target.value,
                                        }))
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        width: "397px",
                                    }}
                                />
                            </div>
                            <div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        className="date-picker-input"
                                        label={t("accounting.fromDate")}
                                        inputFormat="DD/MM/YYYY"
                                        value={filter.fromDate}
                                        onChange={(newValue: Dayjs | null) =>
                                            setFilter(() => ({
                                                ...filter,
                                                fromDate: newValue,
                                            }))
                                        }
                                        renderInput={(params) => (
                                            <BaseDateInput
                                                {...params}
                                                autoComplete="off"
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        className="date-picker-input"
                                        label={t("accounting.endDate")}
                                        inputFormat="DD/MM/YYYY"
                                        value={filter.toDate}
                                        onChange={(newValue: Dayjs | null) =>
                                            setFilter(() => ({
                                                ...filter,
                                                toDate: newValue,
                                            }))
                                        }
                                        renderInput={(params) => (
                                            <BaseDateInput
                                                {...params}
                                                autoComplete="off"
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div>
                                <BaseInput
                                    select
                                    label={t("message.messageStatus")}
                                    size="small"
                                    value={filter.messageStatus || ""}
                                    onChange={(event) =>
                                        setFilter(() => ({
                                            ...filter,
                                            messageStatus: event.target.value,
                                        }))
                                    }
                                    sx={{
                                        width: "250px",
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton
                                                className="select-clear-icon"
                                                sx={{
                                                    visibility: filter.messageStatus ? "visible" : "hidden",
                                                }}
                                                onClick={() =>
                                                    setFilter(() => ({
                                                        ...filter,
                                                        messageStatus: null,
                                                    }))
                                                }
                                            >
                                                <img src={ClearIcon} alt="Clear icon" />
                                            </IconButton>
                                        ),
                                    }}
                                >
                                    {messageStatus.map((option) => (
                                        <MenuItem key={option.label} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </BaseInput>
                            </div>
                        </div>
                        <div className="filter-search-btn">
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <span onClick={() => clearFilter()} className="link-element clear-all-btn">
                                    {t("display.ClearAll")}
                                </span>
                            </div>
                            <SearchButton className="basic-button" type="submit">
                                {t("general.filter")}
                            </SearchButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default MessagesFilter;
